import { Form, Formik } from 'formik';
import { TFunction } from 'i18next';
import React, { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { FaExclamationCircle } from 'react-icons/all';
import { useDispatch, useSelector } from 'react-redux';
import { CustomsInstructionsForHandling } from '../../../../@posturinn/client-react';
import { closeAllModals } from '../../../../actions/modalActions';
import { addShipmentCustomsInformation } from '../../../../actions/shipmentsActions';
import { getCurrentLangPretty } from '../../../../helpers/url/urlHelpers';
import { ReactComponent as Close } from '../../../../icons/close-button.svg';
import IUserProfileReducer from '../../../../interfaces/reducers/userProfileReducer';
import { IStore } from '../../../../reducers';
import Button from '../../../base/button/button';
import FormikCheckbox from '../../../base/forms/formikCheckbox';
import FormikFieldWrapper from '../../../base/forms/formikFieldWrapper';
import { StyledFormikField } from '../../../base/forms/input';
import { A, P } from '../../../base/typography';
import UploadFiles from '../../../uploadFiles/uploadFiles';
import * as StyledModals from '../myShipmentsModals.styles';
import { attachFilesSchema } from './attachFiles.schema';

interface Props {}

const AttachFiles: FC<Props> = () => {
	const { t } = useTranslation('myShipments');
	const dispatch = useDispatch<any>();
	const closeModal = () => {
		dispatch(closeAllModals());
		document.body.style.overflow = 'unset';
	};
	// Prevent body scroll when the modal is open.
	document.body.style.overflow = 'hidden';

	const { modalHeading, shipmentId, page } = useSelector(({ modalReducer }) => modalReducer);
	const userProfileReducer: IUserProfileReducer = useSelector<IStore, IUserProfileReducer>(
		store => store.userProfileReducer
	);
	const { modalSubmitPending, modalSubmitError, modalSubmitErrorDetails } = userProfileReducer;
	const { emailVerified, email } = userProfileReducer?.userProfile;

	const submit = (values: any) => {
		dispatch(
			addShipmentCustomsInformation(
				shipmentId,
				values.additionalInformation,
				values.files,
				values.isGift ? CustomsInstructionsForHandling.Gift : CustomsInstructionsForHandling.IHaveReceipt,
				page,
				values.email
			)
		);
	};

	return (
		<StyledModals.Wrapper>
			<StyledModals.Heading>
				<div>{modalHeading}</div>
				<A onClick={closeModal}>
					<Close />
				</A>
			</StyledModals.Heading>
			{modalSubmitError && (
				<StyledModals.ErrorMessage>
					<FaExclamationCircle />
					<div>{modalSubmitErrorDetails !== '' ? modalSubmitErrorDetails : t('genericError')}</div>
				</StyledModals.ErrorMessage>
			)}
			<Formik
				initialValues={{
					gift: false,
					files: [],
					email: email,
					additionalInformation: '',
					terms: false,
				}}
				enableReinitialize
				validationSchema={attachFilesSchema}
				onSubmit={submit}>
				{({ setFieldValue, isValid }) => (
					<Form>
						<StyledModals.UploadWrapper>
							<UploadFiles name="files" />
						</StyledModals.UploadWrapper>
						<FormikFieldWrapper
							label={`${t('filesAdditionalInformation')} *`}
							name="additionalInformation"
							languageComponent="myShipments"
							smallLabel>
							<StyledFormikField
								maxLength="254"
								type="text"
								id="additionalInformation"
								name="additionalInformation"
								placeholder={t('filesAdditionalInformation')}
							/>
						</FormikFieldWrapper>
						<FormikFieldWrapper
							name="email"
							languageComponent="myShipments"
							smallLabel
							label={`${t('email')} *`}>
							<StyledFormikField
								type="text"
								id="email"
								name="email"
								placeholder={t('email')}
								disabled={emailVerified}
							/>
						</FormikFieldWrapper>
						<FormikFieldWrapper
							name="terms"
							languageComponent="myShipments"
							smallLabel
							label={`${t('terms')}`}>
							<P>{t('acceptTerms')}</P>
							<FormikCheckbox
								name="terms"
								id="terms"
								label={
									<Trans
										t={t as TFunction}
										i18nKey={'termsLinked'}
										components={[
											<A
												href={
													getCurrentLangPretty() === 'is'
														? 'https://posturinn.is/media/5caf3fjo/tollur_skilmalar.pdf'
														: 'https://posturinn.is/media/ysbcfhwx/terms_custom.pdf'
												}
												target="_blank"
											/>,
										]}
									/>
								}
								setFieldValue={setFieldValue}
							/>
						</FormikFieldWrapper>
						<Button primary type="submit" disabled={!isValid} isLoading={modalSubmitPending}>
							{t('submit')}
						</Button>
					</Form>
				)}
			</Formik>
		</StyledModals.Wrapper>
	);
};

export default AttachFiles;
